import { Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ui/theme-provider";
import Home from "@/pages/Home";
import Index from "@/pages/Index";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import Instructors from "@/pages/Instructors";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import NotFound from "@/pages/NotFound";
import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentMessages from "@/pages/student/StudentMessages";
import CourseCatalog from "@/pages/student/CourseCatalog";
import Progress from "@/pages/student/Progress";
import Settings from "@/pages/student/Settings";
import BookingsList from "@/pages/student/BookingsList";
import MyEnrollments from "@/pages/student/MyEnrollments";
import PracticalCourseDetails from "@/pages/student/PracticalCourseDetails";
import PracticalCourseEnrollment from "@/pages/student/PracticalCourseEnrollment";
import TheoryCourseView from "@/pages/student/TheoryCourseView";
import IndividualCourseView from "@/pages/student/IndividualCourseView";
import IndividualCourseEnrollment from "@/pages/student/IndividualCourseEnrollment";
import PaymentHistory from "@/pages/student/PaymentHistory";
import StudentSchedules from "@/pages/student/StudentSchedules";
import StudentSchedule from "@/pages/student/StudentSchedule";
import StudentProfile from "@/pages/student/StudentProfile";
import StudentPayments from "@/pages/student/StudentPayments";
import StudentStories from "@/pages/student/StudentStories";
import InstructorDashboard from "@/pages/instructor/InstructorDashboard";
import InstructorMessages from "@/pages/instructor/InstructorMessages";
import InstructorStudents from "@/pages/instructor/InstructorStudents";
import InstructorProfile from "@/pages/instructor/InstructorProfile";
import InstructorSettings from "@/pages/instructor/InstructorSettings";
import InstructorPayments from "@/pages/instructor/InstructorPayments";
import EnrolledStudents from "@/pages/instructor/EnrolledStudents";
import InstructorScheduleList from "@/pages/instructor/InstructorScheduleList";
import InstructorCourses from "@/pages/instructor/InstructorCourses";
import InstructorAvailability from "@/pages/instructor/InstructorAvailability";
import InstructorStudentPayments from "@/pages/instructor/InstructorStudentPayments";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import UsersManagement from "@/pages/admin/UsersManagement";
import CourseManagement from "@/pages/admin/CourseManagement";
import BookingsManagement from "@/pages/admin/BookingsManagement";
import ContentManagement from "@/pages/admin/ContentManagement";
import InstructorsManagement from "@/pages/admin/InstructorsManagement";
import FinanceManagement from "@/pages/admin/FinanceManagement";
import GalleryManagement from "@/pages/admin/GalleryManagement";
import CoursePreview from "@/pages/admin/CoursePreview";
import InstructorAssignment from "@/pages/admin/InstructorAssignment";
import StudentGallery from "@/pages/StudentGallery";
import InstructorPaymentsManagement from "@/pages/admin/InstructorPaymentsManagement";
import PaymentsManagement from "@/pages/admin/PaymentsManagement";
import LessonHistory from '@/pages/student/LessonHistory';
import BookOnBehalf from "@/pages/admin/BookOnBehalf";
import PaymentReceipt from "@/pages/payment/PaymentReceipt";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminProfile from "@/pages/admin/AdminProfile";
import SystemBackup from "@/pages/admin/SystemBackup";
import PaymentSuccess from "@/pages/payment/PaymentSuccess";
import ChatPage from '@/pages/chat/ChatPage';
import PaymentsPage from '@/pages/admin/PaymentsPage';
import AdminStudentPayments from '@/pages/admin/StudentPayments';
import UpdateStudentData from '@/pages/admin/UpdateStudentData';
import ForgotPassword from "@/pages/auth/ForgotPassword";
import InstructorBookLesson from "@/pages/instructor/InstructorBookLesson";
import PublicBookLesson from "@/pages/PublicBookLesson";
import AreasManagement from "@/pages/admin/AreasManagement";
import MockTestResults from "@/pages/instructor/MockTestResults";
import "./App.css";

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <TooltipProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/index" element={<Index />} />
          <Route path="/home" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/instructors" element={<Instructors />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/gallery" element={<StudentGallery />} />
          <Route path="/receipt/:receiptId" element={<PaymentReceipt />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/book-driving-lesson" element={<PublicBookLesson />} />

          {/* Student Routes */}
          <Route path="/student-dashboard" element={<StudentDashboard />} />
          <Route path="/student-dashboard/messages" element={<StudentMessages />} />
          <Route path="/student-dashboard/courses" element={<CourseCatalog />} />
          <Route path="/student-dashboard/progress" element={<Progress />} />
          <Route path="/student-dashboard/settings" element={<Settings />} />
          <Route path="/student-dashboard/bookings" element={<BookingsList />} />
          <Route path="/student-dashboard/my-enrollments" element={<MyEnrollments />} />
          <Route path="/student-dashboard/payments" element={<StudentPayments />} />
          <Route path="/student-dashboard/payment-history" element={<PaymentHistory />} />
          <Route path="/student-dashboard/schedules" element={<StudentSchedule />} />
          <Route path="/student-dashboard/profile" element={<StudentProfile />} />
          <Route
            path="/student-dashboard/practical-course/:courseId"
            element={<PracticalCourseDetails />}
          />
          <Route
            path="/student-dashboard/practical-course/:courseId/enroll"
            element={<PracticalCourseEnrollment />}
          />
          <Route
            path="/student-dashboard/theory-course/:courseId"
            element={<TheoryCourseView />}
          />
          <Route
            path="/student-dashboard/individual-course/:courseId"
            element={<IndividualCourseView />}
          />
          <Route
            path="/student-dashboard/individual-course/:courseId/enroll"
            element={<IndividualCourseEnrollment />}
          />
          <Route path="/student-dashboard/lesson-history" element={<LessonHistory />} />
          <Route path="/student-dashboard/stories" element={<StudentStories />} />

          {/* Instructor Routes */}
          <Route path="/instructor-dashboard" element={<InstructorDashboard />} />
          <Route path="/instructor-dashboard/messages" element={<InstructorMessages />} />
          <Route path="/instructor-dashboard/students" element={<InstructorStudents />} />
          <Route path="/instructor-dashboard/profile" element={<InstructorProfile />} />
          <Route path="/instructor-dashboard/settings" element={<InstructorSettings />} />
          <Route path="/instructor-dashboard/enrolled-students" element={<EnrolledStudents />} />
          <Route path="/instructor-dashboard/payments" element={<InstructorPayments />} />
          <Route path="/instructor-dashboard/student-payments" element={<InstructorStudentPayments />} />
          <Route path="/instructor-dashboard/schedules" element={<InstructorScheduleList />} />
          <Route path="/instructor-dashboard/courses" element={<InstructorCourses />} />
          <Route path="/instructor-dashboard/availability" element={<InstructorAvailability />} />
          <Route path="/instructor/book-lesson" element={<InstructorBookLesson />} />
          <Route path="/instructor-dashboard/mock-test-results" element={<MockTestResults />} />

          {/* Admin Routes */}
          <Route path="/admin-dashboard" element={<AdminDashboard />} />
          <Route path="/admin-dashboard/users" element={<UsersManagement />} />
          <Route path="/admin-dashboard/users/new" element={<UsersManagement />} />
          <Route path="/admin-dashboard/courses" element={<CourseManagement />} />
          <Route path="/admin-dashboard/course-preview/:courseId" element={<CoursePreview />} />
          <Route path="/admin-dashboard/bookings" element={<BookingsManagement />} />
          <Route path="/admin-dashboard/bookings/new" element={<BookOnBehalf />} />
          <Route path="/admin-dashboard/instructors" element={<InstructorsManagement />} />
          <Route path="/admin-dashboard/finance" element={<FinanceManagement />} />
          <Route path="/admin-dashboard/book-on-behalf" element={<BookOnBehalf />} />
          <Route path="/admin-dashboard/content" element={<ContentManagement />} />
          <Route path="/admin-dashboard/settings" element={<AdminSettings />} />
          <Route path="/admin-dashboard/profile" element={<AdminProfile />} />
          <Route path="/admin-dashboard/gallery" element={<GalleryManagement />} />
          <Route path="/admin-dashboard/areas" element={<AreasManagement />} />
          <Route path="/admin-dashboard/assign-instructor/:enrollmentId" element={<InstructorAssignment />} />
          <Route path="/admin-dashboard/instructor-payments" element={<InstructorPaymentsManagement />} />
          <Route path="/admin-dashboard/payments" element={<PaymentsManagement />} />
          <Route path="/admin-dashboard/student-payments" element={<AdminStudentPayments />} />
          <Route path="/admin-dashboard/update-student-data" element={<UpdateStudentData />} />
          <Route path="/admin/payments" element={<PaymentsPage />} />
          <Route path="/admin-dashboard/system-backup" element={<SystemBackup />} />

          {/* Chat Routes */}
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:threadId" element={<ChatPage />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster />
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
